import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { setTenantResolver } from '@/app/api/client'
import { ApiError } from '@/app/api/errors'
import * as authApi from '@/features/auth/api/auth'
import type { Grant, Me, MembershipSummary } from '@/features/auth/api/auth'

/**
 * Who is signed in, which tenant is selected, and the coarse permission summary
 * (SDD §10.3). Everything here is a UX cache of server state: the backend re-checks
 * session, membership and permission on every request (guardrail §5.4), so nothing in
 * this store is an authorization decision.
 */
const TENANT_KEY = 'fumbo.tenant'

function readSelectedTenant(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY)
  } catch {
    return null
  }
}

export const useSessionStore = defineStore('session', () => {
  const me = ref<Me | null>(null)
  const loaded = ref(false)
  const tenantId = ref<string | null>(readSelectedTenant())
  const grants = ref<Grant[]>([])
  /** BRD-057: the workspace's plan usage; modules null = every module. */
  const plan = ref<authApi.PlanUsage | null>(null)
  const grantsLoadedFor = ref<string | null>(null)
  /** BRD-077: until checked in for the day, only check-in is available. */
  const checkinRequired = ref(false)
  /** BRD-079: a checked-in lead is sent to Team sign-in until the team is signed in. */
  const teamSigninPending = ref(false)
  /** BRD-089: the designated lead of a team today - the ticket queue and Take open. */
  const leadsTeam = ref(false)

  setTenantResolver(() => tenantId.value)

  const isAuthenticated = computed(() => me.value !== null)
  const isPlatformOperator = computed(() => (me.value?.platform_permissions.length ?? 0) > 0)
  const activeMemberships = computed(() =>
    (me.value?.memberships ?? []).filter((m) => m.membership_status === 'active'),
  )
  /** The operator's open support session, as the server reports it (FR-SUP-001). */
  const supportSession = computed(() => me.value?.support_session ?? null)
  /** Every tenant the person may enter: memberships, plus the support session's tenant. */
  const reachableTenants = computed<MembershipSummary[]>(() => {
    const s = supportSession.value
    if (!s || activeMemberships.value.some((m) => m.tenant_id === s.tenant_id)) {
      return activeMemberships.value
    }
    return [
      ...activeMemberships.value,
      {
        tenant_id: s.tenant_id,
        slug: s.slug,
        display_name: s.display_name,
        membership_status: 'active',
        tenant_status: s.tenant_status,
        logo_url: null,
        via: 'support',
      },
    ]
  })
  const tenant = computed(
    () => reachableTenants.value.find((x) => x.tenant_id === tenantId.value) ?? null,
  )
  /** True while the selected tenant is entered through a support session. */
  const inSupportMode = computed(() => tenant.value?.via === 'support')
  /** Server-mandated next step before ordinary work (FR-AUTH-002, FR-AUTH-010). */
  const securityStep = computed<'password' | 'mfa' | null>(() => {
    if (!me.value) return null
    if (me.value.password_change_required) return 'password'
    if (me.value.mfa_required && !me.value.mfa_enrolled) return 'mfa'
    return null
  })
  const permissions = computed(() => new Set(grants.value.map((g) => g.permission)))

  function can(permission: string): boolean {
    return permissions.value.has(permission)
  }

  function setMe(next: Me | null): void {
    me.value = next
    loaded.value = true
    const reachable =
      next &&
      tenantId.value &&
      (next.memberships.some((m) => m.tenant_id === tenantId.value) ||
        next.support_session?.tenant_id === tenantId.value)
    if (next && tenantId.value && !reachable) selectTenant(null)
  }

  /** Refresh from the server; a 401 simply means signed out. */
  async function load(): Promise<Me | null> {
    try {
      setMe(await authApi.fetchMe())
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setMe(null)
      else throw error
    }
    return me.value
  }

  function selectTenant(id: string | null): void {
    tenantId.value = id
    grants.value = []
    plan.value = null
    grantsLoadedFor.value = null
    checkinRequired.value = false
    teamSigninPending.value = false
    leadsTeam.value = false
    try {
      if (id) localStorage.setItem(TENANT_KEY, id)
      else localStorage.removeItem(TENANT_KEY)
    } catch {
      /* private mode: selection lives for this session only */
    }
  }

  async function loadGrants(): Promise<void> {
    if (!tenantId.value) {
      grants.value = []
      plan.value = null
      return
    }
    const result = await authApi.fetchMyPermissions()
    grants.value = result.grants
    plan.value = result.plan
    checkinRequired.value = result.checkinRequired
    teamSigninPending.value = result.teamSigninPending
    leadsTeam.value = result.leadsTeam
    grantsLoadedFor.value = tenantId.value
  }
  /** BRD-057: whether a module is inside the plan (everything is, without a plan). */
  function moduleAllowed(module: string): boolean {
    return plan.value?.modules == null || plan.value.modules.includes(module)
  }

  async function signOut(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      setMe(null)
      selectTenant(null)
    }
  }

  return {
    me,
    loaded,
    tenantId,
    tenant,
    grants,
    plan,
    moduleAllowed,
    grantsLoadedFor,
    checkinRequired,
    teamSigninPending,
    leadsTeam,
    isAuthenticated,
    isPlatformOperator,
    activeMemberships,
    supportSession,
    reachableTenants,
    inSupportMode,
    securityStep,
    can,
    setMe,
    load,
    selectTenant,
    loadGrants,
    signOut,
  }
})
